import { useEffect, useMemo, useState } from 'react';
import {
  destinatairesRelanceTransporteur, motifRelance, REGLAGES_PAR_DEFAUT, remplirRelanceTransporteur,
} from '@mosaik/core';
import { ErreurApi } from '../api/client';
import { useRelance } from '../api/hooks';
import type { CommandeListee, DocumentReglages } from '../api/types';
import { RelanceHorsMail } from './RelanceHorsMail';
import { useToasts } from './Toasts';
import { Repousser } from './Repousser';
import { RelancesEnvoyees } from './RelancesEnvoyees';

/**
 * Fenêtre de rédaction de la relance transporteur.
 *
 * **Le mail part chez le fournisseur, pas chez le transporteur.** Les
 * transporteurs que Mosaik croise n'ont pas de service litige : le contrat de
 * transport est celui du fournisseur, qui a choisi son transporteur et reste le
 * seul à pouvoir le faire bouger. Les destinataires sont donc exactement ceux
 * du mail de commande — mêmes interlocuteurs, même fil. Seule exception :
 * HIOLLE, dont l'envoi parti se réclame directement au transporteur (cf.
 * `destinatairesRelanceTransporteur`).
 *
 * Le modèle vient de Réglages, jamais du code. Une adresse manquante n'est pas
 * remplacée par une invention : la fenêtre nomme la société concernée et laisse
 * saisir, parce qu'un mail parti à une adresse devinée se perd sans bruit.
 *
 * Une commande partie en plusieurs colis n'envoie qu'UNE relance : elle nomme
 * tous les colis bloqués — ou, faute de blocage, tous ceux encore en route.
 */
export function RelanceTransporteur({
  commande, reglages, onFermer,
}: {
  commande: CommandeListee;
  reglages: DocumentReglages | undefined;
  onFermer: () => void;
}) {
  const { ajouter } = useToasts();
  const relance = useRelance();

  const modele = useMemo(() => {
    const aujourdhui = new Date().toISOString().slice(0, 10);
    const texte = remplirRelanceTransporteur(
      { objet: reglages?.objetRelance ?? '', corps: reglages?.corpsRelance ?? '' },
      commande, commande.calcul.livraisonEstimee?.date ?? null, aujourdhui,
    );
    // Le destinataire suit le colis réclamé : chez HIOLLE, c'est son
    // transporteur qu'on relance, et un colis supplémentaire peut en avoir un autre.
    const vise = texte.colis[0];
    return {
      ...texte,
      ...destinatairesRelanceTransporteur(reglages ?? REGLAGES_PAR_DEFAUT, commande.fournisseurs, {
        transporteur: vise?.transporteur ?? commande.transporteur, dateExpedition: commande.dateExpedition,
        tracking: vise?.tracking ?? commande.tracking,
      }),
    };
  }, [commande, reglages]);

  const [to, setTo] = useState(modele.destinataires);
  const [objet, setObjet] = useState(modele.objet);
  const [corps, setCorps] = useState(modele.corps);
  const [erreur, setErreur] = useState<string | null>(null);

  useEffect(() => {
    setTo(modele.destinataires); setObjet(modele.objet); setCorps(modele.corps);
  }, [modele]);

  useEffect(() => {
    const auClavier = (e: KeyboardEvent) => { if (e.key === 'Escape') onFermer(); };
    document.addEventListener('keydown', auClavier);
    return () => document.removeEventListener('keydown', auClavier);
  }, [onFermer]);

  /**
   * Envoie la relance.
   *
   * Le mail d'abord, l'enregistrement ensuite : sans mail parti, personne
   * n'a relancé le transporteur, et rien ne doit le laisser croire. La fenêtre
   * reste ouverte, texte intact, avec la raison de l'échec.
   */
  async function envoyer() {
    setErreur(null);
    try {
      const r = await relance.mutateAsync({
        id: commande.id,
        corps: {
          cible: 'transporteur',
          // Cette fenêtre sert deux réclamations : la date d'expédition d'une
          // société qui EST le transport, et la livraison d'un envoi déjà parti.
          motif: motifRelance(commande),
          destinataire: to,
          objet,
          message: corps,
        },
      });

      if (!r.brouillon.depose) {
        setErreur(
          `Relance non enregistrée : ${r.brouillon.erreur ?? "le mail n'a pas pu être envoyé."}`,
        );
        return;
      }

      ajouter(
        r.brouillon.simule
          ? 'Relance enregistrée, mais Outlook est en mode local : aucun mail envoyé.'
          : `${commande.transporteur ?? 'Transporteur'} relancé — mail envoyé à ${to} depuis contact@.`,
        r.brouillon.simule ? 'bad' : 'ok',
      );
      onFermer();
    } catch (e) {
      setErreur(e instanceof ErreurApi ? e.message : "L'enregistrement a échoué.");
    }
  }

  return (
    <div className="modal" onClick={(e) => { if (e.target === e.currentTarget) onFermer(); }}>
      <div className="box" role="dialog" aria-modal="true" aria-label={`Relance transporteur ${commande.ref}`}>
        <div className="mhead">
          <h2>Relance transporteur — {commande.ref}</h2>
          <button className="x" onClick={onFermer} aria-label="Fermer">&times;</button>
        </div>

        <div className="mbody">
          <RelancesEnvoyees commande={commande} cible="transporteur" />
          {modele.colis.length > 1 && (
            <p className="hint" style={{ marginTop: 0 }}>
              Une seule relance pour les {modele.colis.length} colis concernés :{' '}
              {modele.colis.map((k) => k.tracking).join(', ')}.
            </p>
          )}
          <div className="field">
            <label htmlFor="m-to">Destinataire</label>
            <input
              id="m-to" value={to} onChange={(e) => setTo(e.target.value)}
              placeholder="adresse du fournisseur ou du transporteur"
            />
            {modele.inconnus.length > 0 && (
              <p className="err">
                Aucune adresse à l&apos;annuaire pour {modele.inconnus.join(', ')}. Renseignez-la
                dans Réglages pour qu&apos;elle soit pré-remplie la prochaine fois.
              </p>
            )}
            {!modele.destinataires && !modele.inconnus.length && (
              <p className="err">
                Cette commande ne porte aucun fournisseur : il n&apos;y a personne à relancer
                automatiquement. Saisissez l&apos;adresse à la main.
              </p>
            )}
          </div>
          <div className="field">
            <label htmlFor="m-obj">Objet</label>
            <input id="m-obj" value={objet} onChange={(e) => setObjet(e.target.value)} />
          </div>
          <div className="field">
            <label htmlFor="m-corps">Message</label>
            <textarea id="m-corps" rows={15} value={corps} onChange={(e) => setCorps(e.target.value)} />
          </div>
          {erreur && <p className="err" role="alert">{erreur}</p>}
          <p style={{ fontSize: 11.5, color: 'var(--ink-3)' }}>
            Adressée au fournisseur, qui est le donneur d&apos;ordre du transporteur. Modèle et
            annuaire modifiables dans Réglages. Le mail{' '}
            <strong>part dès que vous cliquez sur Envoyer</strong>, depuis contact@ : relisez-le
            ici, il n&apos;y a pas d&apos;autre relecture. La relance n&apos;est enregistrée que si
            le mail est bien parti.
          </p>
        </div>

        <div className="mfoot">
          <Repousser commande={commande} serre />
          <RelanceHorsMail
            commande={commande} cible="transporteur" motif={motifRelance(commande)} onFait={onFermer}
          />
          <button
            type="button" className="btn pri" disabled={relance.isPending}
            onClick={() => void envoyer()}
          >
            {relance.isPending ? <><span className="spin" /> Envoi…</> : 'Envoyer la relance'}
          </button>
        </div>
      </div>
    </div>
  );
}
